<template>
  <div>
    <b-card no-body>
      <b-row class="m-1">
        <b-col cols="6" class="d-flex align-items-center">
          <v-select
            v-model="perPage"
            :clearable="false"
            class="per-page-selector d-inline-block w-auto"
            :options="[5, 10, 20, 50]"
            @input="onPerPageChange"
          />
        </b-col>
        <b-col cols="6" class="d-flex justify-content-end">
          <b-button variant="primary" @click="$router.push({ name: 'pages-pricing-add' })">
            Add Plan
          </b-button>
        </b-col>
      </b-row>

      <div class="m-2">
        <b-table
          :items="plans"
          :fields="fields"
          :busy="isLoading"
          primary-key="id"
          responsive
          show-empty
          empty-text="No plans found"
        >
          <template #table-busy>
            <div class="text-center text-secondary my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>

          <template #cell(srNo)="row">
            {{ (currentPage - 1) * perPage + row.index + 1 }}
          </template>

          <template #cell(price)="row">
            {{ formatAmount(row.item.price, row.item.currency) }}
          </template>

          <template #cell(price_yearly)="row">
            {{ formatAmount(row.item.price_yearly, row.item.currency) }}
          </template>

          <template #cell(stripe)="row">
            <small class="d-block text-muted">
              M: {{ row.item.stripe_price_id_monthly || '—' }}
            </small>
            <small class="d-block text-muted">
              Y: {{ row.item.stripe_price_id_yearly || '—' }}
            </small>
          </template>

          <template #cell(is_active)="row">
            <span class="mypoda-status-tag" :class="planIsActive(row.item) ? 'status-active' : 'status-inactive'">
              {{ planIsActive(row.item) ? 'Active' : 'Inactive' }}
            </span>
          </template>

          <template #cell(created_at)="row">
            {{ formatCreatedAt(row.item.created_at) }}
          </template>

          <template #cell(is_popular)="row">
            <b-badge v-if="row.item.is_popular" variant="light-primary">
              Popular
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(actions)="row">
            <div class="plan-actions-cell">
              <feather-icon
                icon="EditIcon"
                size="18"
                class="cursor-pointer mr-1"
                @click="$router.push({ name: 'pages-pricing-edit', params: { id: row.item.id } })"
              />
              <feather-icon
                icon="Trash2Icon"
                size="18"
                class="cursor-pointer"
                @click="deletePlan(row.item.id)"
              />
            </div>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-1">
          <span class="text-muted">Total: {{ totalRows }}</span>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            @input="fetchPlans"
          >
            <template #prev-text><feather-icon icon="ChevronLeftIcon" size="16" /></template>
            <template #next-text><feather-icon icon="ChevronRightIcon" size="16" /></template>
          </b-pagination>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { adminPanelApi } from '@/services/adminPanelApi'
import { unwrapPaginatedList } from '@/utils/adminApiHelpers'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  data() {
    return {
      plans: [],
      fields: [
        { key: 'srNo', label: '#' },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'price', label: 'Monthly', sortable: true },
        { key: 'price_yearly', label: 'Yearly', sortable: true },
        { key: 'duration', label: 'Duration (days)', sortable: true },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'stripe', label: 'Stripe Price IDs' },
        { key: 'is_popular', label: 'Popular', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions', thClass: 'text-center', tdClass: 'text-center' },
      ],
      isLoading: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
    }
  },
  mounted() {
    this.fetchPlans()
  },
  methods: {
    formatCreatedAt(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    },
    planIsActive(plan) {
      if (plan?.is_active != null) return !!plan.is_active
      return plan?.status == 1 || plan?.status === true
    },
    formatAmount(value, currency = 'usd') {
      const number = Number(value || 0)
      const code = String(currency || 'usd').toUpperCase()
      const symbol = code === 'USD' ? '$' : code === 'INR' ? '₹' : ''
      const formatted = Number.isNaN(number) ? '0' : number.toFixed(2)
      return symbol ? `${symbol} ${formatted}` : `${formatted} ${code}`
    },
    onPerPageChange() {
      this.currentPage = 1
      this.fetchPlans()
    },
    async fetchPlans() {
      this.isLoading = true
      try {
        const res = await adminPanelApi.plans({
          page: this.currentPage,
          per_page: this.perPage,
        })
        const { items, total } = unwrapPaginatedList(res)
        this.plans = items
        this.totalRows = total
      } catch {
        this.$swal({
          title: 'Error',
          text: 'Unable to fetch plans',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
      } finally {
        this.isLoading = false
      }
    },
    deletePlan(id) {
      this.$swal({
        title: 'Are you sure?',
        text: 'This plan will be removed. Existing Stripe subscriptions are not cancelled automatically.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        try {
          await adminPanelApi.deletePlan(id)
          await this.fetchPlans()
        } catch {
          this.$swal({
            title: 'Error',
            text: 'Unable to delete plan',
            icon: 'error',
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.plan-actions-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-width: 72px;
}
</style>
